import React, { useState, useRef, useEffect, useCallback } from "react";
import { debounce } from "../../utils/debounce";
import styles from "./AddressModal.module.css";

interface AddressModalProps {
  isOpen: boolean;
  isEditing: boolean;
  address: string;
  onClose: () => void;
  // onEdit: () => void;
  onConfirm: () => void;
  onAddressChange: (address: string) => void;
  onAddressInput: (address: string) => void;
  onAlternativeNameChange: (alternativeName: string) => void;
  isLocationClarification?: boolean;
}

export const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  isEditing,
  address,
  //onClose,
  // onEdit,
  onConfirm,
  onAddressChange,
  onAddressInput,
  onAlternativeNameChange,
  isLocationClarification = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [alternativeNameValue, setAlternativeNameValue] = useState("");
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInitialFocusRef = useRef(false);

  const debouncedAddressInput = useCallback(
    debounce((value: string) => {
      onAddressInput(value);
    }, 800),
    [onAddressInput]
  );

  // Обновляем инпут при смене режима редактирования И при изменении адреса
  useEffect(() => {
    console.log("🔄 useEffect инпута:", { isEditing, address });
    if (isEditing) {
      setInputValue(address || "");
      // Фокус только при первом открытии модалки
      if (!isInitialFocusRef.current) {
        isInitialFocusRef.current = true;
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      }
    } else {
      // Сбрасываем флаг при закрытии
      isInitialFocusRef.current = false;
    }
  }, [isEditing, address]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    debouncedAddressInput(value);
  };

  useEffect(() => {
    if (isEditing && address && address !== inputValue) {
      // Обновляем значение БЕЗ фокуса - только если инпут не в фокусе
      if (document.activeElement !== inputRef.current) {
        setInputValue(address);
      }
    }
  }, [address, isEditing, inputValue]);

  const handleSave = () => {
    if (isLocationClarification && alternativeNameValue.trim() === "") {
      setHasError(true);
      return;
    }

    onAddressChange(inputValue);
    onAlternativeNameChange(alternativeNameValue);
    onConfirm();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.addressOverlay}>
      <div className={styles.addressModal}>
        {/* Заголовок */}
        <div className={styles.addressHeader}>
          <div className={styles.addressHeaderContent}>
            <h3 className={styles.addressTitle}>Уточните адрес</h3>
          </div>

          {/* Кнопка карандаша только в режиме просмотра */}
          {/* {!isEditing && false && (
            <button className={styles.editButton} onClick={() => {}}>
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.75 4.00001L15 9.25001M5.95208 18.2021L0.7977 13.0477M5.68934 18.25H1.5C1.30109 18.25 1.11032 18.171 0.96967 18.0303C0.829018 17.8897 0.75 17.6989 0.75 17.5V13.3107C0.75 13.2122 0.769399 13.1147 0.80709 13.0237C0.844781 12.9327 0.900026 12.85 0.96967 12.7803L12.2197 1.53034C12.3603 1.38969 12.5511 1.31067 12.75 1.31067C12.9489 1.31067 13.1397 1.38969 13.2803 1.53034L17.4697 5.71968C17.6103 5.86033 17.6893 6.0511 17.6893 6.25001C17.6893 6.44892 17.6103 6.63969 17.4697 6.78034L6.21967 18.0303C6.15003 18.1 6.06735 18.1552 5.97635 18.1929C5.88536 18.2306 5.78783 18.25 5.68934 18.25Z"
                  stroke="#8C8A92"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )} */}
        </div>

        {/* Контент */}
        <div className={styles.addressContent}>
          {isEditing ? (
            /* Режим редактирования - показываем инпуты */
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <input
                  ref={inputRef}
                  type="text"
                  className={styles.addressInput}
                  value={inputValue}
                  onChange={handleInputChange}
                  placeholder=" "
                  inputMode="text"
                  enterKeyHint="done"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      inputRef.current?.blur();
                    }
                  }}
                />
                <label className={styles.inputLabel}>Адрес</label>
              </div>

              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  className={`${styles.addressInput} ${
                    hasError ? styles.errorInput : ""
                  }`}
                  value={alternativeNameValue}
                  onChange={(e) => {
                    setAlternativeNameValue(e.target.value);
                    if (hasError && e.target.value.trim() !== "") {
                      setHasError(false);
                    }
                  }}
                  placeholder=" "
                  inputMode="text"
                  enterKeyHint="done"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                />
                <label className={styles.inputLabel}>
                  Как еще называют это место ?
                </label>
              </div>
            </div>
          ) : null}

          {/* Кнопка действия */}
          <button
            className={styles.addressActionButton}
            onClick={isEditing ? handleSave : onConfirm}
          >
            {isEditing ? "Сохранить локацию" : "Все верно"}
          </button>
        </div>
      </div>
    </div>
  );
};
